/**
 * The shared savings sufficiency check: required = floor + multiplier ×
 * shortfall (register §4 — the multiplier applies to the SHORTFALL), with
 * the 6-month holding period producing a computed eligible_from date when
 * it is the only thing outstanding (NR-1).
 */
import { addMonthsISO, compareISODate } from "../dates.js";
import type { CaseFacts } from "../types.js";
import type { PredicateOutcome } from "./predicate.js";

export function savingsSufficiency(args: {
  facts: CaseFacts;
  threshold: number;
  income: number;
  floor: number;
  multiplier: number;
  holdingMonths: number;
  extraFigures?: Record<string, number>;
}): PredicateOutcome {
  const { facts, threshold, income, floor, multiplier, holdingMonths } = args;

  const shortfall = Math.max(0, threshold - income);
  const savings = facts.finances.cash_savings;
  if (savings === null) {
    return {
      status: "PENDING_EVIDENCE",
      because: ["FIN.THRESHOLD"],
      notes: ["The amount of savings held, and the date they have been held since, are needed."],
    };
  }

  const required = floor + multiplier * shortfall;
  const headroom = savings.amount - required;
  const figures: Record<string, number> = {
    threshold,
    floor,
    multiplier,
    shortfall,
    required_savings: required,
    savings_held: savings.amount,
    headroom,
    ...args.extraFigures,
  };

  if (savings.amount < required) {
    return {
      status: "NOT_MET",
      figures,
      because: ["FIN.THRESHOLD"],
      notes: ["The savings held are below the amount the rules ask for at this stage."],
    };
  }

  const holdingCompletes = addMonthsISO(savings.held_since, holdingMonths);
  if (compareISODate(facts.as_of, holdingCompletes) < 0) {
    return {
      status: "NOT_MET",
      figures,
      eligible_from: holdingCompletes,
      because: ["FIN.THRESHOLD"],
      notes: [
        "The savings reach the amount the rules ask for, and the 6-month holding period completes on the date shown.",
      ],
    };
  }

  return { status: "MET", figures, because: ["FIN.THRESHOLD"] };
}
